import React from 'react';

import { usePageContentId } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';

type UseIsAIEnabledForContentProps = {
	contentId?: string;
};

/**
 * A hook created as part of the AI Data Security Policy integration to evaluate content-based AI-enablement.
 * Returns an object containing `isAIEnabledForContent` and `isRovoEnabledForContent`.
 *
 * Previously, a feature gate was used to determine if the AI access policy was enabled.
 * When enabled, a GraphQL call was made to ensure that an AI block policy has not been placed on the
 * specified contentId, in addition to checking the values of `isAdminHubAIEnabled` and `isRovoEnabled`.
 * However, due to the prolonged deprioritization of the AI access policy project, the gate has been removed.
 *
 * Now, by default, values from `useSessionData` are returned directly:
 * `isAdminHubAIEnabled` (whether or not AI is enabled for the tenant)
 * and `isRovoEnabled` (whether or not Rovo is enabled for the tenant) respectively.
 *
 * TODO: Coordinate with teams dependent on this hook to remove it and update all callers
 * to leverage `useSessionData` directly (if possible).
 *
 * @param {Object} params - The hook's input object
 * @param {string} params.contentId The contentId of the piece of content to determine AI enablement of
 */
export const useIsAIEnabledForContent = (
	_props: UseIsAIEnabledForContentProps = {},
): {
	isAIEnabledForContent: boolean;
	isRovoEnabledForContent: boolean;
} => {
	const { isAdminHubAIEnabled, isRovoEnabled, isRovoLLMEnabled } = useSessionData();
	const shouldShowRovo = fg('rovo_use_rovo_llm_enabled_check') ? isRovoLLMEnabled : isRovoEnabled;

	return {
		isAIEnabledForContent: isAdminHubAIEnabled,
		isRovoEnabledForContent: shouldShowRovo,
	};
};

/**
 * Higher-order component that adds the ability to check for content-based AI enablement by leveraging
 * the `useIsAIEnabledForContent` hook.
 *
 * @template P - The props type of the wrapped component
 * @param {React.ComponentType<P>} WrappedComponent - The component to wrap
 * @returns {React.ComponentType<P & {
 *   isAIEnabledForContent: boolean,
 *   isRovoEnabledForContent: boolean
 * }>} Component with AI enablement status passed down as props
 */
export function withIsAIEnabledForContent(WrappedComponent: React.ComponentType<any>) {
	return function EnhancedComponent(props: any): React.JSX.Element {
		const [contentId] = usePageContentId();
		const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
			contentId: contentId ?? '',
		});

		return (
			<WrappedComponent
				{...props}
				isAIEnabledForContent={isAIEnabledForContent}
				isRovoEnabledForContent={isRovoEnabledForContent}
			/>
		);
	};
}
